<script setup lang="ts">
import { computed, h, markRaw, ref, watch, type Component } from 'vue'
import { useRoute, useRouter, type RouteMeta } from 'vue-router'
import { useRoles } from '@/composables/useRoles'
import {
    NAV_GROUPS,
    NAV_LINKS,
    requirementsForGroup,
    requirementsForRoute,
    satisfies,
} from '@/lib/nav/groups'
import { Icon } from '@iconify/vue'
import {
    Sidebar as UiSidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarHeader,
    SidebarItem,
    SidebarTrigger,
} from '@LITTA-CO/ui-vue'
import SidebarLogo from './ui/SidebarLogo.vue'

interface Props {
    userName?: string | null
}

interface Emits {
    (event: 'logout'): void
}

interface SidebarLeaf {
    kind: 'item'
    name: string
    label: string
    icon: Component
    order: number
    href: string
    /** Leaves Beehive, so it opens in a new tab and `name` is only a key. */
    external?: boolean
}

interface SidebarGroupEntry {
    kind: 'group'
    key: string
    label: string
    icon: Component
    order: number
    children: SidebarEntry[]
}

type SidebarEntry = SidebarLeaf | SidebarGroupEntry

const props = withDefaults(defineProps<Props>(), {
    userName: 'User',
})

const emit = defineEmits<Emits>()

const route = useRoute()
const router = useRouter()

const { roles } = useRoles()

const groupAllowed = (key: string): boolean =>
    satisfies(requirementsForGroup(key), roles.value)

const isVisible = (meta: RouteMeta): boolean =>
    satisfies(requirementsForRoute(meta), roles.value)

const COLLAPSED_STORAGE_KEY = 'sidebarCollapsed'
const COLLAPSED_BY_DEFAULT = true

function readStoredCollapsed(): boolean {
    try {
        const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY)
        return stored === null ? COLLAPSED_BY_DEFAULT : stored === 'true'
    } catch {
        return COLLAPSED_BY_DEFAULT
    }
}

const collapsed = ref(readStoredCollapsed())

watch(collapsed, value => {
    try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(value))
    } catch {
        // Nothing to do: the sidebar still works, it just won't be remembered.
    }
})

function iconFor(iconName: string): Component {
    return markRaw({
        name: 'SidebarNavIcon',
        render() {
            return h(Icon, { icon: iconName })
        },
    })
}

const personIcon = iconFor('material-symbols:person')
const logoutIcon = iconFor('material-symbols:logout')

const stagingQuery = computed(() =>
    route.query.staging === 'true' ? { staging: 'true' } : {},
)

const entries = computed<SidebarEntry[]>(() => {
    const groupChildren = new Map<string, SidebarLeaf[]>()
    const topLevel: SidebarEntry[] = []

    for (const record of router.getRoutes()) {
        if (!record.meta.icon || typeof record.name !== 'string') continue
        if (!isVisible(record.meta)) continue

        const leaf: SidebarLeaf = {
            kind: 'item',
            name: record.name,
            label: record.meta.sidebarLabel ?? record.meta.title ?? record.name,
            icon: iconFor(record.meta.icon),
            order: record.meta.sidebarOrder ?? Number.POSITIVE_INFINITY,
            href: router.resolve({
                name: record.name,
                query: { ...record.meta.sidebarQuery, ...stagingQuery.value },
            }).href,
        }

        const groupKey = record.meta.sidebarGroup
        if (groupKey) {
            const children = groupChildren.get(groupKey) ?? []
            children.push(leaf)
            groupChildren.set(groupKey, children)
        } else {
            topLevel.push(leaf)
        }
    }

    for (const link of NAV_LINKS) {
        const children = groupChildren.get(link.group) ?? []
        children.push({
            kind: 'item',
            name: link.href,
            label: link.label,
            icon: iconFor(link.icon),
            order: link.order,
            href: link.href,
            external: true,
        })
        groupChildren.set(link.group, children)
    }

    const built = new Map<string, SidebarGroupEntry>()
    for (const group of NAV_GROUPS) {
        if (!groupAllowed(group.key)) continue
        built.set(group.key, {
            kind: 'group',
            key: group.key,
            label: group.label,
            icon: iconFor(group.icon),
            order: group.order,
            children: groupChildren.get(group.key) ?? [],
        })
    }

    /* An empty subgroup is dropped rather than attached, so a group nobody has
     * access to leaves no bare heading behind. Its parent is then pruned too
     * if the subgroup was all it held. */
    for (const group of NAV_GROUPS) {
        if (!group.parent) continue
        const child = built.get(group.key)
        const parent = built.get(group.parent)
        if (child && parent && hasContent(child)) parent.children.push(child)
    }

    for (const group of NAV_GROUPS) {
        if (group.parent) continue
        const entry = built.get(group.key)
        if (entry && hasContent(entry)) topLevel.push(entry)
    }

    return sortEntries(topLevel)
})

/** A group earns a place only if something inside it is visible. */
function hasContent(entry: SidebarGroupEntry): boolean {
    return entry.children.some(child =>
        child.kind === 'group' ? hasContent(child) : true,
    )
}

function sortEntries(entries: SidebarEntry[]): SidebarEntry[] {
    for (const entry of entries) {
        if (entry.kind === 'group') sortEntries(entry.children)
    }
    return entries.sort((first, second) => first.order - second.order)
}

/** Whether the current route is anywhere inside this entry. */
function containsActive(entry: SidebarEntry): boolean {
    return entry.kind === 'group'
        ? entry.children.some(containsActive)
        : isActive(entry)
}

function leavesOf(group: SidebarGroupEntry): SidebarLeaf[] {
    return group.children.filter(
        (child): child is SidebarLeaf => child.kind === 'item',
    )
}

function isActive(item: SidebarLeaf): boolean {
    if (item.external) return false
    return route.matched.some(record => record.name === item.name)
}

function navigate(item: SidebarLeaf) {
    router.push(item.href)
}

/** Plain left-clicks route in-app; modified clicks are left to the browser. */
function handleLinkClick(event: MouseEvent, item: SidebarLeaf) {
    if (item.external) return
    if (event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigate(item)
}

function linkAttrs(item: SidebarLeaf) {
    return item.external
        ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
        : { href: item.href }
}

function handleLogout() {
    emit('logout')
}
</script>

<template>
    <UiSidebar
        v-model:collapsed="collapsed"
        class="app-sidebar"
    >
        <SidebarHeader v-slot="{ collapsed: isCollapsed }">
            <SidebarLogo
                class="min-w-0 flex-1 overflow-hidden transition-opacity duration-300"
                :class="isCollapsed && 'opacity-0'"
            />
            <SidebarTrigger />
        </SidebarHeader>

        <SidebarContent class="select-none">
            <template v-for="entry in entries">
                <SidebarGroup
                    v-if="entry.kind === 'group'"
                    :key="entry.key"
                    :icon="entry.icon"
                    :label="entry.label"
                    :default-open="containsActive(entry)"
                >
                    <template v-for="child in entry.children">
                        <SidebarGroup
                            v-if="child.kind === 'group'"
                            :key="child.key"
                            :icon="child.icon"
                            :label="child.label"
                            :default-open="containsActive(child)"
                        >
                            <SidebarItem
                                v-for="leaf in leavesOf(child)"
                                :key="leaf.name"
                                :icon="leaf.icon"
                                :label="leaf.label"
                                as="a"
                                v-bind="linkAttrs(leaf)"
                                :active="isActive(leaf)"
                                @click="handleLinkClick($event, leaf)"
                            />
                        </SidebarGroup>
                        <SidebarItem
                            v-else
                            :key="child.name"
                            :icon="child.icon"
                            :label="child.label"
                            as="a"
                            v-bind="linkAttrs(child)"
                            :active="isActive(child)"
                            @click="handleLinkClick($event, child)"
                        />
                    </template>
                </SidebarGroup>
                <SidebarItem
                    v-else
                    :key="entry.name"
                    :icon="entry.icon"
                    :label="entry.label"
                    as="a"
                    v-bind="linkAttrs(entry)"
                    :active="isActive(entry)"
                    @click="handleLinkClick($event, entry)"
                />
            </template>
        </SidebarContent>

        <SidebarFooter class="flex-col gap-2">
            <SidebarItem
                as="div"
                :icon="personIcon"
                :label="props.userName || 'User'"
            />
            <SidebarItem
                :icon="logoutIcon"
                label="Logout"
                @click="handleLogout"
            />
        </SidebarFooter>
    </UiSidebar>
</template>
